<!-- //
组件可视化开发-表单设计
@auth xuyd
@date 2022-11-27
//-->
<template>
  <div class="screen-design" style="height: calc(100vh); position: relative;">
    <div class="design-layout">
      <!-- 组件库 -->
      <div class="define-container" v-show="showSideBar">
        <el-scrollbar class="scroll-container">
          <el-collapse class="template-container" v-model="selectedCollapse" :accordion="false">
            <el-collapse-item v-for="(item, index) in widgetTemplateMenu" :key="index" :name="index">
              <div slot="title" class="title">{{ item.title }}</div>
              <div class="template-list">
                <div class="template-item"
                     v-for="subItem in item.children"
                     :class="{dragItemLayout: true, templateWidgetList: true}"
                     :key="subItem.id"
                     draggable="true"
                     @dragstart="handleTemplateDragStart(subItem, $event)"
                     @dragend="handleTemplateDragEnd"
                >
                  <svg-icon :icon="subItem.icon"/>
                  <div class="item-title">{{ subItem.title }}</div>
                  <div class="item-tip" v-if="subItem.maxUse > 0">{{ subItem.maxUse }}</div>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </el-scrollbar>
      </div>
      <div class="split-bar split-bar-left" @click="showSideBar = !showSideBar">
        <el-icon :class="showSideBar ? 'el-icon-arrow-left': 'el-icon-arrow-right'"/>
      </div>

      <!-- 组件设计工作台 -->
      <div class="workbench-container">
        <WbToolbar @click="handleToolCmd" :toolbar-data="toolbarData"/>

        <WbRulerTool class="workbench-ruler"
                     :step-length="50"
                     :parent="true"
                     :position="'relative'"
                     :is-scale-revise="true">
          <div class="grid-line">
            <div id="workbench" class="workbench"
                 :style="{
                transform: `scale(${toolbarData.scale})`,
                width: workbenchProps.width + 'px',
                height: workbenchProps.height + 'px',
                'background-color': workbenchProps.backgroundColor,
              }"
                 @click.self="handleWorkbenchClick"
                 @dragover="handleWidgetDragOver($event)"
                 @drop="handleWidgetDrop($event)"
            >

              <WidgetView class="widget-item"
                           ref="refDraggableWidget"
                           v-for="(widgetItem, index) in widgetViewList"
                           :widget-data="widgetItem"
                           :active-new-add="selectedWidget ? (selectedWidget.id === widgetItem.id) : false"
                           :scale="toolbarData.scale"
                           :key="index"
                           @onActivated="handleWidgetActivated(index, widgetItem)"
                           @contextmenu.prevent.native="handleWidgetCtxMenu(widgetItem, $event)"
                           @mousedown.prevent.native="handleWidgetMouseDown(index, widgetItem, $event)"
              >
                <component :is="widgetItem.widgetProps.widgetViewName" :widget-data="widgetItem.widgetProps"/>
              </WidgetView>
            </div>
          </div>
        </WbRulerTool>
      </div>
    </div>
    <div class="props-container">
      <div class="split-bar split-bar-right" @click="showPropsBar = !showPropsBar">
        <el-icon :class="showPropsBar ? 'el-icon-arrow-right': 'el-icon-arrow-left'"/>
      </div>
      <div class="props-form" v-show="showPropsBar">
        <div style="width: 100%; height: 100%; overflow: auto;">
          <component ref="refPropsForm"
                     v-if="selectedWidget"
                     :is="selectedWidget.widgetProps.widgetFormName"
                     :widget-data="selectedWidget.widgetProps">
          </component>
          <component v-else :is="'WidgetWorkbench'" :workbench-props="workbenchProps"/>
        </div>
      </div>
    </div>
    <WbContextMenu ref="refContextMenu" :widget-view-list="widgetViewList"/>
  </div>
</template>

<script>
import {widgetPackage} from '../widget/index.js'; // 组件库
import {widgetMenuList} from '../widget/widgetMenu.js'; // 组件菜单
import * as WidgetUtil from '../utils/util.js';
import DataFormApi from '@/api/datavisual/dataForm.api.js';
import WbToolbar from "../components/workbench/wb-toolbar.vue";
import WbContextMenu from "../components/workbench/wb-context-menu.vue";
import WbRulerTool from "vue-ruler-tool";
import WidgetWorkbench from "../components/widget-props/wb-workbench-props.vue";
import WidgetView from "../components/widget-view/index.vue";

// 工具栏配置的组件注册{FormXXX:{}, ViewXXX: {}}
const regComponents = WidgetUtil.registerWidget(widgetMenuList, widgetPackage);

export default {
  name: 'ViewScreenDesign',
  components: {
    ...regComponents
    , WbToolbar
    , WbRulerTool
    , WidgetWorkbench
    , WidgetView
    , WbContextMenu
  },
  data() {
    return {
      formId: null,
      widgetTemplateMenu: this.$util.listToTree(widgetMenuList),
      widgetTemplateMap: this.$util.listToMap(widgetMenuList, {idField: 'widgetName'}),
      draggingWidgetName: null, // 拖动的组件模板名
      draggingWidgetOffsetX: null, // 拖动的组件偏移x
      draggingWidgetOffsetY: null, // 拖动的组件偏移y
      selectedCollapse: 0, // 默认打开的组件面板

      widgetViewList: [], // 右侧工作区的所有View组件
      selectedWidget: null,// 选择的组件对象
      toolbarData: {scale: 0.6},// 工具栏参数
      workbenchProps: {// 工作台组件数据
        id: null,
        width: 1920, // 大屏设计宽度
        height: 1080, // 大屏设计高度
        backgroundColor: '@dv_background_bg', // 大屏背景色
      },
    }
  },
  computed: {
    showSideBar: {
      get() {
        return this.$store.getters.showSideBar;
      },
      set(v) {
        this.$store.dispatch('doShowSideBar', v)
      }
    },
    showPropsBar: {
      get() {
        return this.$store.getters.showPropsBar;
      },
      set(v) {
        this.$store.dispatch('doShowPropsBar', v)
      }
    }
  },
  async created() {
    this.formId = this.$route.query.formId;
    await this.getWorkbenchProps()
    this.getList()
  },

  methods: {
    /**
     * 查询大屏信息
     */
    async getWorkbenchProps() {
      await DataFormApi.getFormInfo(this.formId).then(resp => {
        this.workbenchProps = resp.data;
        console.log('--------workbenchProps--------->', this.workbenchProps)
      })
    },

    /**
     * 查询页面组件数据
     */
    getList() {
      DataFormApi.selectWidgetList(this.formId).then(resp => {
        let dataList = WidgetUtil.api2Vnode(this.widgetTemplateMap, resp.data)
        this.widgetViewList.push(...dataList);
        // console.log('--------加载db数据--------->', data, this.widgetViewList)
      })
    },

    // 左侧开始拖动组件
    handleTemplateDragStart(item, event) {
      this.draggingWidgetName = item.widgetName;
      this.draggingWidgetOffsetX = event.offsetX;
      this.draggingWidgetOffsetY = event.offsetY;
      // console.log("---handleTemplateDragStart---->", this.draggingWidgetName)
    },

    // 左侧拖动组件结束
    handleTemplateDragEnd(val) {
      // console.log("---handleTemplateDragEnd---->", val, this.widgetViewList)
    },

    // 进入工作区
    handleWidgetDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";
    },

    /**
     * 拖动结束，设置根据x,y坐标放置
     */
    handleWidgetDrop(event) {
      let eventX = event.clientX;
      let eventY = event.clientY;
      let wbPosition = this.getElementTopLeftById('workbench')
      // 计算相对于工作区位置，且去掉在widget内部的偏移量
      // 得到组件左上角相对于工作区坐标
      let widgetInWorkbenchTop = eventY - wbPosition.top - this.draggingWidgetOffsetY;
      let widgetInWorkbenchLeft = eventX - wbPosition.left - this.draggingWidgetOffsetX;
      let position = {top: widgetInWorkbenchTop, left: widgetInWorkbenchLeft};

      // 复制并设置Id
      let widgetClone = WidgetUtil.copyVnodeByName(this.widgetTemplateMap[this.draggingWidgetName]);
      widgetClone.id = null;
      widgetClone.formId = this.formId;
      widgetClone.widgetProps.viewProps.top = position.top;
      widgetClone.widgetProps.viewProps.left = position.left;
      DataFormApi.addWidget(widgetClone).then(resp => {
        this.widgetViewList.push(resp.data);
        this.selectedWidget = resp.data;
        // console.log("----当前工作台组件：----->", this.widgetViewList);
      })
    },

    /**
     * 获取element在屏幕的位置
     * @param id
     * @returns {{top: number, left: number}}
     */
    getElementTopLeftById(id) {
      let el = document.getElementById(id)
      let top = 0
      let left = 0
      if (el != null) {
        top = el.getBoundingClientRect().top
        left = el.getBoundingClientRect().left
      }
      return {top: top, left: left}
    },

    /**
     * 打开上下文菜单
     */
    handleWidgetCtxMenu(widgetItem, e) {
      console.log("----handleWidgetCtxMenu--->", widgetItem, e.target)
      const menuMinWidth = 150
      const offsetLeft = this.$el.getBoundingClientRect().left // container margin left
      const offsetWidth = this.$el.offsetWidth // container width
      const maxLeft = offsetWidth - menuMinWidth + offsetLeft // left boundary
      const eventX = e.clientX
      let left;
      if (eventX > maxLeft) {
        left = maxLeft
      } else {
        left = eventX
      }
      let top = e.clientY
      this.selectedWidget = widgetItem
      this.$refs.refContextMenu.openMenu({left: left, top: top, widgetData: widgetItem});
    },

    /**
     * 点击工作台
     */
    handleWorkbenchClick() {
      console.log('点击了工作台')
      this.selectedWidget = null;
    },

    /**
     * 点击组件
     */
    handleWidgetMouseDown(index, widgetItem, event) {
      // event.preventDefault();
      // event.stopPropagation();
      const widgets = this.$refs.refDraggableWidget;
      for (let i = 0; i < widgets.length; i++) {
        if (i === index) {
          // widgets[i].setActive(true);
        } else {
          // widgets[i].setActive(false);
        }
      }
      this.selectedWidget = widgetItem;
      this.$refs.refContextMenu.closeMenu();
    },

    /**
     * 监听组件
     */
    handleWidgetActivated(index, widgetItem, activated) {
      console.log("----handleWidgetActivated--->", widgetItem, activated)
    },


    /**
     * 工具栏操作，保存，预览等
     *
     * @param cmd
     * @param value
     */
    handleToolCmd({cmd, value}) {
      switch (cmd) {
        case 'save':
          console.log("==========保存数据===========>", {
            workbenchData: this.workbenchProps,
            widgetViewList: this.widgetViewList
          })
          DataFormApi.saveForm(this.workbenchProps);
          DataFormApi.updateWidgetList(this.widgetViewList).then(resp => {
            this.$modal.msgSuccess("保存成功");
          });
          break;
        case 'preview': {
          let routeData = this.$router.resolve({name: 'ViewFormPreview'});
          window.open(routeData.href + '?formId=' + this.formId, '_blank');
        }
          break;
        case 'import':
          this.$modal.msg('开发中');
          break;
        case 'export':
          this.$modal.msg('开发中');
          break;
        default:
          break;
      }
    },
  }
}
</script>

<style lang="less" scoped>
@import './variables.less';

.screen-design {
  .el-form-item__label {
    color: @dv_form_label_color !important;
    font-size: 12px !important;
  }

  .el-form-item {
    margin-bottom: 5px !important;
  }

  .el-input__inner {
    background-color: @dv_form_input_background !important;
    border: 1px solid @dv_form_input_border !important;
    color: @dv_form_input_color;
    padding: 0 8px;
  }

  .el-input-group__append {
    border-left: 0;
  }

  .el-input-group__prepend {
    border-right: 0;
  }

  .el-input-number__decrease,
  .el-input-number__increase,
  .el-input-group__append,
  .el-input-group__prepend {
    color: @dv_form_input_color !important;
    background-color: @dv_form_input_border !important;
    border: 1px solid @dv_form_input_border !important;
  }

  .el-tabs--border-card > .el-tabs__content {
    padding: 10px 10px;
  }

  .el-tabs--border-card {
    background-color: @dv_background_fe !important;
    border: 0;
    box-shadow: 0 0 0 0;

    .el-tabs__header {
      background-color: @dv_background_bg !important;
      border-width: 0;

      .el-tabs__item {
        border-width: 0;
      }

      .el-tabs__item.is-active {
        background-color: @dv_background_fe !important;
      }
    }
  }
}
</style>

<style lang="less" scoped>
@import './variables.less';

.design-layout {
  width: 100%;
  min-width: 1200px;
  height: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  background-color: @dv_background_bg;
}

// template组件区域
.define-container {
  width: 150px;
  height: 100%;
  padding: 10px 5px 10px 10px;

  .scroll-container {
    height: 100%;
  }

  .scroll-container .el-scrollbar__wrap {
    overflow-x: hidden;
    overflow-y: scroll;
  }

  :deep(.el-collapse),
  :deep(.el-collapse-item__header),
  :deep(.el-collapse-item__wrap) {
    background-color: transparent;
    border-bottom: 0;
    border-top: 0;
    color: @dv_widget_label_color;
  }


  .template-container {
    height: auto;
  }

  .title {
    display: block;
    padding: 10px 0;
    font-size: 14px;
    font-weight: 600;
    color: @dv_widget_label_color;
  }

  .template-list {
    display: flex;
    flex-direction: column;
    justify-content: left;
    margin-bottom: 10px;

    .template-item {
      width: 120px;
      height: 40px;
      padding: 4px;
      font-size: 14px;
      //background-color: #fdfdfd;
      color: @dv_widget_label_color;
      border: solid 1px @dv_widget_border;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: left;

      .item-icon {
      }

      .item-title {
        margin-left: 5px;
      }

      .item-tip {
      }
    }

    .template-item:hover {
      cursor: pointer;
      color: @dv_widget_active_color;
      background-color: @dv_widget_active_background;
      box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.22);
      -webkit-box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.22);
    }

    .template-item + .template-item {
      margin-top: 10px;
    }
  }
}

.split-bar {
  display: flex;
  align-items: center;
  padding: 6px 2px;
  color: @dv_split_color;
  font-size: 12px;
  background-color: @dv_split_background;
  cursor: pointer;

  &:hover {
    color: @dv_split_active_color;
    background-color: @dv_split_active_background;
  }

  &.split-bar-left {
    border-left: 1px solid @dv_split_border;
  }

  &.split-bar-right {
    border-right: 1px solid @dv_split_border;
  }
}

// 可视区域容器
.workbench-container {
  flex: 1;
  min-width: 500px;
  height: 100%;
  display: flex;
  flex-direction: column;

  .workbench-ruler {
    flex: 1;
  }

  .grid-line {
    background-size: 10px 10px, 10px 10px;
    background-image: linear-gradient(#f1f1f1 1px, transparent 0),
    linear-gradient(90deg, #f1f1f1 1px, transparent 0);
  }

  .workbench {
    height: 100%;
    overflow: auto;
    transform-origin: 0 0;
    transition: transform 0s; /* 添加过渡动画 */
    background-position: 0 0;
    //background-size: 100% 100%;
    background-repeat: initial;
    background-attachment: initial;
    background-origin: initial;
    background-clip: initial;
    /*cursor: pointer;*/
  }

  .workbench-view {
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .widget-item {
    padding: 0;
    margin: 0;
    position: absolute;
    cursor: move;
  }
}

.props-container {
  position: absolute;
  display: flex;
  flex-direction: row;
  height: 100%;
  z-index: 1000;
  top: 0;
  right: 0;
  bottom: 0;

  .props-form {
    width: 350px;
    min-width: 350px;
    height: 100%;
    background-color: @dv_background_bg;
  }
}

</style>
